import { useEffect, useRef, useMemo } from 'react';

import { marketLiveUpdatesManager, getMarketChannelName } from '../services/marketLiveUpdates.service';

export { getMarketChannelName };

type UseMarketPusherOptions = {
  marketIds: string[];
  source: string;
  enabled?: boolean;
};

type UseMarketPusherReturn = {
  activeSubscriptions: number;
};

export function useMarketPusher({
  marketIds,
  source,
  enabled = true,
}: UseMarketPusherOptions): UseMarketPusherReturn {
  const previousMarketIdsRef = useRef<Set<string>>(new Set());

  const marketIdSet = useMemo(() => new Set(marketIds), [marketIds]);

  useEffect(() => {
    if (!enabled) {
      for (const marketId of previousMarketIdsRef.current) {
        marketLiveUpdatesManager.unsubscribe(source, marketId);
      }
      previousMarketIdsRef.current = new Set();
      return;
    }

    const prev = previousMarketIdsRef.current;
    const curr = marketIdSet;

    for (const marketId of curr) {
      if (!prev.has(marketId)) {
        marketLiveUpdatesManager.subscribe(source, marketId);
      }
    }

    for (const marketId of prev) {
      if (!curr.has(marketId)) {
        marketLiveUpdatesManager.unsubscribe(source, marketId);
      }
    }

    previousMarketIdsRef.current = new Set(curr);
  }, [enabled, marketIdSet, source]);

  useEffect(
    () => () => {
      for (const marketId of previousMarketIdsRef.current) {
        marketLiveUpdatesManager.unsubscribe(source, marketId);
      }
      previousMarketIdsRef.current = new Set();
    },
    [source]
  );

  return {
    activeSubscriptions: marketIdSet.size,
  };
}
